import React from 'react';
import FlexTable from 'components/FlexTable';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, UncontrolledTooltip } from 'reactstrap';
import __ from 'utils/i18n';
import { calculatePlanStatus } from '../../../Plans/_shared/planStatusUtils';
import '../../../styles.scss';

const getSubscribersColumns = (
  onRequestCardUpdate, 
  onPauseSubscription, 
  onCancelSubscription, 
  onReactivateSubscription,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
  allPageSelected,
  somePageSelected,
) => {
  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatFrequency = (frequency) => {
    if (!frequency) return '';
    
    // Normalizar la frecuencia a PascalCase (primera letra mayúscula)
    const normalizedFrequency = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();
    
    const frequencies = {
      'Daily': __('dashboard.sections.recurringPayments.plans.table.frequencies.daily'),
      'Weekly': __('dashboard.sections.recurringPayments.plans.table.frequencies.weekly'),
      'Monthly': __('dashboard.sections.recurringPayments.plans.table.frequencies.monthly'),
      'Quarterly': __('dashboard.sections.recurringPayments.plans.table.frequencies.quarterly'),
      'Yearly': __('dashboard.sections.recurringPayments.plans.table.frequencies.yearly'),
    };
    return frequencies[normalizedFrequency] || frequency;
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'Active': 'success',
      'Trial': 'success',
      'Cancelled': 'danger',
      'Paused': 'warning',
      'Expired': 'secondary',
      'PastDue': 'information',
    };
    return statusClasses[status] || 'secondary';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'Active': __('dashboard.sections.recurringPayments.subscribers.table.statuses.active'),
      'Trial': __('dashboard.sections.recurringPayments.subscribers.table.statuses.trial'),
      'Cancelled': __('dashboard.sections.recurringPayments.subscribers.table.statuses.cancelled'),
      'Paused': __('dashboard.sections.recurringPayments.subscribers.table.statuses.paused'),
      'Expired': __('dashboard.sections.recurringPayments.subscribers.table.statuses.expired'),
      'PastDue': __('dashboard.sections.recurringPayments.subscribers.table.statuses.pastDue'),
    };
    return statusTexts[status] || status;
  };

  return [
    {
      Header: (
        <input
          type="checkbox"
          checked={allPageSelected}
          ref={(el) => {
            if (el) el.indeterminate = somePageSelected && !allPageSelected;
          }}
          onChange={onToggleSelectAll}
          onClick={(e) => e.stopPropagation()}
          aria-label={__('dashboard.sections.recurringPayments.subscribers.bulk.selectAllPage')}
        />
      ),
      Cell: ({ original }) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(original.subscriptionId)}
          onChange={() => onToggleSelected(original.subscriptionId)}
          onClick={(e) => e.stopPropagation()}
          aria-label={__('dashboard.sections.recurringPayments.subscribers.bulk.selectRow')}
        />
      ),
      width: 40,
      sortable: false,
      cancelClick: true,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.idRef'),
      accessor: 'externalReference',
      Cell: ({ original }) => original.externalReference || '-',
      width: 150,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.email'),
      accessor: 'customer.email',
      Cell: ({ original }) => (
        <span className="table-email-cell">{original.customer?.email || '-'}</span>
      ),
      width: 250,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.name'),
      accessor: 'customer.fullName',
      Cell: ({ original }) => (
        <span className="table-text-wrap-cell">{original.customer?.fullName || '-'}</span>
      ),
      width: 200,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.dni'),
      accessor: 'customer.documentNumber',
      Cell: ({ original }) => original.customer?.documentNumber || '-',
      width: 150,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.plan'),
      accessor: 'plan.name',
      Cell: ({ original, index }) => {
        const planName = original.plan?.name || '-';
        const amount = original.plan?.amount;
        const currency = original.plan?.currency;
        const frequency = original.plan?.billingFrequency;
        const pricingType = original.plan?.pricingType;
        const plan = original.plan;
        
        // Calcular estado del plan
        const planStatus = plan ? calculatePlanStatus(plan) : null;
        const isChargeDay = planStatus === 'ChargeDay';
        const isNextToCharge = planStatus === 'NextToCharge';
        const isNoAmounts = planStatus === 'NoAmounts';
        const isPendingCharge = planStatus === 'PendingCharge';
        const isActive = planStatus === 'Active';
        const isCancelled = plan?.status === 'Inactive';

        // Determinar badge class y texto
        let badgeClass = 'secondary';
        let statusText = '';
        
        if (isCancelled) {
          badgeClass = 'secondary';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.cancelled');
        } else if (isChargeDay) {
          badgeClass = 'primary';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.chargeDay');
        } else if (isNextToCharge) {
          badgeClass = 'warning';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.nextToCharge');
        } else if (isNoAmounts) {
          badgeClass = 'danger';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.noAmounts');
        } else if (isPendingCharge) {
          badgeClass = 'warning';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.pendingCharge');
        } else if (isActive) {
          badgeClass = 'success';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.active');
        }
        
        const infoTooltipId = `plan-status-info-tooltip-${original.subscriptionId}-${index}`;
        
        // Mostrar ícono de información para Active, NextToCharge y NoAmounts si tiene billingDay
        const showInfoIcon = plan && planStatus && (isActive || isNextToCharge || isNoAmounts) && plan.billingDay;
        
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className="table-text-wrap-cell">{planName}</span>
              {plan && planStatus && (
                <span className={`badge badge-${badgeClass} ml-2`} style={{ fontSize: '0.75rem' }}>
                  {statusText}
                  {showInfoIcon && (
                    <>
                      <i 
                        id={infoTooltipId}
                        className="mdi mdi-information-outline ml-1" 
                        style={{ fontSize: '12px', cursor: 'pointer' }}
                      />
                      <UncontrolledTooltip placement="top" target={infoTooltipId}>
                        {__('dashboard.sections.recurringPayments.plans.detail.billingDayFormat', { day: plan.billingDay })}
                      </UncontrolledTooltip>
                    </>
                  )}
                </span>
              )}
            </div>
            {pricingType === 'fixed' && amount && (
              <small className="text-muted">
                {formatCurrency(amount, currency)}
                {frequency && `/${formatFrequency(frequency)}`}
              </small>
            )}
            {pricingType === 'dynamic' && (
              <small className="text-muted">
                {__('dashboard.sections.recurringPayments.plans.table.variable')}
              </small>
            )}
          </div>
        );
      },
      width: 300,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.status'),
      accessor: 'status',
      Cell: ({ original }) => {
        const badgeClass = getStatusBadgeClass(original.status);
        const statusText = getStatusText(original.status);
        const isCancelled = original.status === 'Cancelled';
        const cancellationReason = isCancelled && original.metadata?.cancellation_reason;
        const tooltipId = `cancellation-reason-${original.subscriptionId}`;
        
        return (
          <span className={`badge badge-${badgeClass}`}>
            {statusText}
            {cancellationReason && (
              <>
                <i 
                  id={tooltipId}
                  className="mdi mdi-information-outline ml-1"
                  style={{ fontSize: '14px', cursor: 'pointer', color: 'white' }}
                ></i>
                <UncontrolledTooltip placement="top" target={tooltipId}>
                  {cancellationReason}
                </UncontrolledTooltip>
              </>
            )}
          </span>
        );
      },
      width: 150,
    },
    {
      Header: __('dashboard.sections.recurringPayments.subscribers.table.actions'),
      Cell: ({ original }) => {
        const isPaused = original.status === 'Paused';
        const isCancelled = original.status === 'Cancelled';

        // Si es pausada, mostrar botón de Reactivar + dropdown
        if (isPaused) {
          return (
            <div className="text-right d-flex align-items-center justify-content-end">
              <Button
                color="primary"
                outline
                size="sm"
                className="font-weight-bold d-inline-flex align-items-center justify-content-center mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onReactivateSubscription && onReactivateSubscription(original);
                }}
              >
                <i className="mdi mdi-refresh mr-1"></i>
                {__('dashboard.sections.recurringPayments.subscribers.actions.reactivate')}
              </Button>
              
              <UncontrolledDropdown>
                <DropdownToggle 
                  tag="button" 
                  className="btn btn-link text-dark p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="mdi mdi-dots-vertical mdi-24px"></i>
                </DropdownToggle>
                <DropdownMenu right className="recurring-payments-dropdown-menu">
                  <DropdownItem onClick={(e) => {
                    e.stopPropagation();
                    onRequestCardUpdate && onRequestCardUpdate(original);
                  }}>
                    {__('dashboard.sections.recurringPayments.subscribers.actions.requestCardUpdate')}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem className="text-danger" onClick={(e) => {
                    e.stopPropagation();
                    onCancelSubscription && onCancelSubscription(original);
                  }}>
                    {__('dashboard.sections.recurringPayments.subscribers.actions.cancelSubscription')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        }

        // Si es cancelada, no mostrar nada
        if (isCancelled) {
          return null;
        }

        // Para suscripciones activas, trial y pastDue, mostrar solo dropdown
        return (
          <UncontrolledDropdown>
            <DropdownToggle 
              tag="button" 
              className="btn btn-link text-dark p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="mdi mdi-dots-vertical mdi-24px"></i>
            </DropdownToggle>
            <DropdownMenu right className="recurring-payments-dropdown-menu">
              <DropdownItem onClick={(e) => {
                e.stopPropagation();
                onRequestCardUpdate && onRequestCardUpdate(original);
              }}>
                {__('dashboard.sections.recurringPayments.subscribers.actions.requestCardUpdate')}
              </DropdownItem>
              <DropdownItem divider />
              {(original.status === 'Active' || original.status === 'Trial') && (
                <>
                  <DropdownItem onClick={(e) => {
                    e.stopPropagation();
                    onPauseSubscription && onPauseSubscription(original);
                  }}>
                    {__('dashboard.sections.recurringPayments.subscribers.actions.pauseSubscription')}
                  </DropdownItem>
                </>
              )}
              <DropdownItem className="text-danger" onClick={(e) => {
                e.stopPropagation();
                onCancelSubscription && onCancelSubscription(original);
              }}>
                {__('dashboard.sections.recurringPayments.subscribers.actions.cancelSubscription')}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
      width: 180,
      sortable: false,
      cancelClick: true,
    },
  ];
};

export const SubscribersTable = ({
  subscribers,
  onRowClick,
  onRequestCardUpdate,
  onPauseSubscription,
  onCancelSubscription,
  onReactivateSubscription,
  selectedIds = [],
  onToggleSelected,
  onToggleSelectAll,
}) => {
  const pageIds = (subscribers || []).map((item) => item.subscriptionId);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id));

  const columns = getSubscribersColumns(
    onRequestCardUpdate,
    onPauseSubscription,
    onCancelSubscription,
    onReactivateSubscription,
    selectedIds,
    onToggleSelected,
    onToggleSelectAll,
    allPageSelected,
    somePageSelected,
  );

  const handleDetails = (data) => {
    onRowClick && onRowClick(data);
  };

  return (
    <div className="table-container">
      {subscribers && subscribers.length > 0 && (
        <FlexTable
          data={subscribers}
          columns={columns}
          onRowClick={({ index }) => handleDetails(subscribers[index])}
        />
      )}
    </div>
  );
};
